import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { AlertTriangle, ShieldAlert, X } from "lucide-react";
import type { CleanupJobView, PocTenantView } from "@ubisafe/shared";
import { api, ApiClientError } from "../lib/api";

interface CleanupMarkCleanModalProps {
  tenant: PocTenantView;
  job: CleanupJobView;
  onClose: () => void;
  onSaved: (job: CleanupJobView) => void;
}

export function CleanupMarkCleanModal({ tenant, job, onClose, onSaved }: CleanupMarkCleanModalProps) {
  const titleId = useId();
  const descriptionId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [confirmation, setConfirmation] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const matches = confirmation === tenant.identifier;

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!matches || !acknowledged || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      onSaved(await api.markCleanupAsClean(tenant.identifier, job.id, confirmation));
    } catch (requestError) {
      setError(requestError instanceof ApiClientError ? requestError.message : "Não foi possível marcar o ambiente como limpo.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget && !submitting) onClose();
    }}>
      <section
        className="modal-card cleanup-confirmation-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
      >
        <header className="modal-card__header">
          <div className="modal-card__title">
            <span className="modal-card__icon cleanup-mark-clean-modal__icon"><ShieldAlert aria-hidden="true" /></span>
            <div>
              <span className="eyebrow eyebrow--dark">Conclusão manual auditada</span>
              <h2 id={titleId}>Marcar ambiente como limpo</h2>
            </div>
          </div>
          <button className="icon-button" type="button" onClick={onClose} disabled={submitting} aria-label="Fechar confirmação">
            <X aria-hidden="true" />
          </button>
        </header>

        <form onSubmit={submit} className="cleanup-confirmation-form">
          <div className="inline-alert inline-alert--warning" id={descriptionId} role="note">
            <AlertTriangle aria-hidden="true" />
            <div>
              <strong>{job.ignoredCount} registro(s) permanecerão como pendência.</strong>
              <span>Esta ação encerrará o ciclo atual e permitirá iniciar uma nova POC. Os registros ignorados, erros e verificações continuarão armazenados para auditoria.</span>
            </div>
          </div>

          <label className="checkbox-field cleanup-pending-acknowledgement">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(event) => setAcknowledged(event.target.checked)}
            />
            <span>Compreendo que o ambiente será considerado limpo mesmo com os resíduos registrados acima.</span>
          </label>

          <label className="form-field cleanup-confirmation-field">
            <span>Digite <strong>{tenant.identifier}</strong> para confirmar</span>
            <input
              ref={inputRef}
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              autoComplete="off"
              spellCheck={false}
              aria-invalid={Boolean(confirmation) && !matches}
              placeholder={tenant.identifier}
            />
            {confirmation && !matches && <small className="field-error">O Identificador deve ser digitado exatamente.</small>}
          </label>

          {error && <div className="inline-alert inline-alert--error" role="alert">{error}</div>}

          <footer className="modal-actions">
            <button className="secondary-button" type="button" onClick={onClose} disabled={submitting}>Cancelar</button>
            <button className="danger-button" type="submit" disabled={!matches || !acknowledged || submitting}>
              {submitting ? "Registrando…" : "Marcar como limpo"}
            </button>
          </footer>
        </form>
      </section>
    </div>
  );
}
